import React, { useState, useMemo, useRef } from 'react';
import { Box, Container, Grid, Typography, Stack, Menu, MenuItem, Tooltip, Paper, Button } from '@mui/material';
import IconButton from 'components/@extended/IconButton';
import { DownloadOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import SideBySideOverview from './SideBySideOverview';
import CompetitiveIndex from './CompetitiveIndex';
import FeatureMatrix from './FeatureMatrix';
import HeatmapScorecard from './HeatmapScorecard';
import PositioningMatrices from './PositioningMatrices';
// import ReportDownloadMenu from './ReportDownloadMenu';
import exportSbsDocx from 'utils/exportSbsDocx';
import { openModal } from 'store/reducers/actions';
import { motion } from 'framer-motion';
import useAntiTamperBlur from 'hooks/useAntiTamperBlur';

const SideBySideMain = () => {
  // Data & access control
  const analysisResults = useSelector((state) => state.competitorsReducer.competitorsAnalysisResults);
  const canAccessSbs = analysisResults?.access?.features?.side_by_side_analysis?.enabled ?? true;
  const reportMeta = analysisResults?.side_by_side_analysis?.analysis?.report_metadata;
  const plan = useSelector((state) => state.proAccountReducer?.plan);
  const isFreePlan = plan === 'Free' || plan === 'Trial' || !plan;
  const trialWasActivated = useSelector((state) => state.proAccountReducer?.trial_was_activated);
  const userCountry = useSelector((state) => state.proAccountReducer?.country);
  const restrictedCountries = ['IN', 'VN', 'CN', 'BD', 'PK', 'ID'];
  const trialAllowed = !trialWasActivated && !restrictedCountries.includes(userCountry);
  const dispatch = useDispatch();

  // Download menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (!canAccessSbs) return; // gated
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const fileName = useMemo(() => {
    const base = reportMeta?.title || reportMeta?.market || 'sbs-competitive-report';
    return `${String(base).replace(/\s+/g, '_')}.docx`;
  }, [reportMeta]);

  const handleDownloadDocx = async () => {
    try {
      await exportSbsDocx(analysisResults, fileName);
    } finally {
      handleClose();
    }
  };

  const handleTrialClick = () => {
    dispatch(
      openModal({
        title: 'Unlock Competitors Analysis',
        subtitle: 'Upgrade to Pro to view all insights across competitors.',
        trial: trialAllowed
      })
    );
  };

  const Gate = ({ children, cta }) => {
    const contentBlurRef = useRef(null);
    const overlayBlurRef = useRef(null);
    useAntiTamperBlur(contentBlurRef, overlayBlurRef, { blurPx: 5 });
    if (!isFreePlan) return children;
    return (
      <Box sx={{ position: 'relative' }}>
        <Box ref={contentBlurRef} sx={{ filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.85 }}>
          {children}
        </Box>
        <Paper
          ref={overlayBlurRef}
          elevation={3}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 3,
            backdropFilter: 'blur(3px)',
            backgroundColor: 'rgba(255,255,255,0.6)'
          }}
        >
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.35 }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 6,
                p: { xs: 2, md: 3 },
                width: '92%',
                maxWidth: 720,
                textAlign: 'left'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {cta?.title || 'Unlock full report'}
              </Typography>
              {Array.isArray(cta?.bullets) && cta.bullets.length > 0 && (
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {cta.bullets.map((b, i) => (
                    <Grid key={i} item xs={12} sm={6}>
                      <Typography variant="body2">• {b}</Typography>
                    </Grid>
                  ))}
                </Grid>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleTrialClick}
                  component={motion.button}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  sx={{ px: 3, py: 1.25, background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)', boxShadow: 'none' }}
                >
                  {trialAllowed ? 'Start Free Trial' : 'Unlock Now'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Cancel anytime
                </Typography>
              </Box>
            </Box>
          </motion.div>
        </Paper>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom>
              Side-by-Side Competitive Report
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" paragraph>
              Comprehensive competitive analysis based on multiple parameters across key market players
            </Typography>
          </Box>
          <Box>
            <Tooltip title={canAccessSbs ? 'Download' : 'Upgrade required'}>
              <span>
                <IconButton size="large" onClick={handleClick} disabled={!canAccessSbs}>
                  <DownloadOutlined />
                </IconButton>
              </span>
            </Tooltip>
            <Menu
              id="sbs-download-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              PaperProps={{
                sx: { border: 1, borderColor: 'secondary.light', borderRadius: 1 }
              }}
            >
              <MenuItem onClick={handleDownloadDocx}>Download as .docx</MenuItem>
            </Menu>
          </Box>
        </Stack>

        {/* Content Area - All components displayed vertically */}
        <Grid container spacing={4}>
          <Grid item xs={12} id="section-sbs-overview">
            {!isFreePlan && null}
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
              Overview
            </Typography>
            <Gate cta={{ title: 'Unlock Competitive Overview', bullets: ['Summary metrics', 'Key insights', 'Company snapshots'] }}>
              <SideBySideOverview />
            </Gate>
          </Grid>
          <Grid item xs={12} id="section-competitive-index">
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
              Competitive Index
            </Typography>
            <Gate cta={{ title: 'Unlock Competitive Index', bullets: ['Weighted scoring', 'Rationale & parameters', 'Benchmarks'] }}>
              <CompetitiveIndex />
            </Gate>
          </Grid>

          <Grid item xs={12} id="section-feature-matrix">
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
              Feature Matrix
            </Typography>
            <Gate cta={{ title: 'Unlock Feature Matrix', bullets: ['Capabilities by competitor', 'Unique differentiators'] }}>
              <FeatureMatrix />
            </Gate>
          </Grid>

          <Grid item xs={12} id="section-heatmap-scorecard">
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
              Heatmap Scorecard
            </Typography>
            <Gate cta={{ title: 'Unlock Heatmap Scorecard', bullets: ['Scores across parameters', 'Strengths & weaknesses'] }}>
              <HeatmapScorecard />
            </Gate>
          </Grid>

          <Grid item xs={12} id="section-positioning-matrices">
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
              Positioning Matrices
            </Typography>
            <Gate cta={{ title: 'Unlock Positioning Matrices', bullets: ['Price vs Features positioning', '2x2 comparisons'] }}>
              <PositioningMatrices />
            </Gate>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default SideBySideMain;
